import classNames from 'classnames'
import { AdminOnlyRing } from 'components/AdminOnlyRing'
import { Spinner } from 'components/Spinner'
import {
  BreakoutButton,
  type ButtonKind,
} from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { DropdownMenu } from 'radix-ui'
import type { Placement } from '@floating-ui/react'

type Props = React.HtmlHTMLAttributes<HTMLDivElement>

export const FloatingIconActionButton = observer(function FloatingActionButton({
  className,
  label,
  size,
  buttonClassName,
  actions,
  Icon,
  kind,
  dropdownClassName,
  menuSide = 'bottom',
  buttonTestId,
  'aria-label': ariaLabel,
  ...props
}: Props & {
  Icon: React.ComponentType<{ size: number }>
  label?: string
  actions: Array<MenuButtonProps | AdminRingGroup>
  kind?: ButtonKind
  size?: 'small' | 'large'
  buttonClassName?: string
  dropdownClassName?: string
  menuSide?: Parameters<typeof DropdownMenu.Content>[0]['side']
  buttonTestId?: string
  'aria-label'?: string
}) {
  return (
    <div className={className} {...props}>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger asChild>
          <BreakoutButton
            aria-label={ariaLabel}
            data-testid={buttonTestId}
            kind={kind}
            size={size}
            icon={<Icon aria-hidden size={18} />}
            className={classNames(
              { 'rounded-full border-none shadow-sm': !!label },
              buttonClassName
            )}
            disabled={actions.length === 0}
          >
            {label}
          </BreakoutButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side={menuSide}
            align="end"
            sideOffset={10}
            className={classNames(
              `z-20 flex flex-col gap-1 text-nowrap rounded-[16px] bg-surface px-4 py-4 shadow-3xl-floating`,
              dropdownClassName
            )}
          >
            {actions.map((action) => {
              if ('isAdminRing' in action) {
                const visibleItems = action.items.filter(
                  (item) => item.hide !== true
                )
                if (visibleItems.length === 0) return null
                return (
                  <AdminOnlyRing
                    key={action.key}
                    label={action.label}
                    onlyShowIf={action.onlyShowIf}
                    ringClassName="mt-2 flex flex-col gap-1"
                  >
                    {visibleItems.map((item) => (
                      <MenuButton {...item} key={item.key} />
                    ))}
                  </AdminOnlyRing>
                )
              }
              if (action.hide === true) return null
              return <MenuButton {...action} key={action.key} />
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  )
})

export type MenuButtonProps = {
  key: string
  Icon: React.ComponentType<{ size: number; className?: string }>
  text: string | React.ReactNode
  onClick: () => void
  isLoading?: boolean
  disabled?: boolean
  tooltip?: string
  hide?: boolean
  tooltipPlacement?: Placement
  className?: string
  dataTestId?: string
}

type AdminRingGroup = {
  isAdminRing: true
  key: string
  label: string
  onlyShowIf: boolean
  items: MenuButtonProps[]
}

const MenuButton = ({
  Icon,
  text,
  tooltip = '',
  onClick,
  isLoading,
  disabled,
  tooltipPlacement,
  className,
  dataTestId,
}: MenuButtonProps) => {
  return (
    <DropdownMenu.Item
      onSelect={(e) => {
        // Due to the previous implementation, the default behavior is to NOT
        // close the menu when the item is selected.
        e.preventDefault()
        if (!isLoading) {
          onClick()
        }
      }}
      disabled={disabled || isLoading}
      className={classNames(
        'flex w-full flex-row items-center gap-1 rounded-lg px-4 py-2 hover:bg-core-tertiary focus-visible:bg-core-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
        disabled ? 'text-on-surface-disabled' : '',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className
      )}
    >
      <BreakoutTooltip
        content={tooltip}
        enabled={!!tooltip}
        placement={tooltipPlacement}
      >
        <div className="flex w-full gap-2" data-testid={dataTestId}>
          {isLoading ? (
            <Spinner aria-hidden size={0.938} /> // 15px as rem
          ) : disabled ? (
            <Icon aria-hidden size={15} className="text-on-surface-disabled" />
          ) : (
            <Icon aria-hidden size={15} />
          )}
          <strong className="text-label-small">{text}</strong>
        </div>
      </BreakoutTooltip>
    </DropdownMenu.Item>
  )
}
